// src/pages/BenchmarkReport.jsx
// Phase R9 — Business Intelligence Report
// Shown in Statements.jsx after AFS PDF download. Unique vs CaseWare/Draftworx.
import { useState } from 'react'
import { generateBenchmark } from '../api/kailux'

import Icon from '../components/Icon'

import { T, alpha } from '../theme/theme'
const STATUS_META = {
  above:   { label:'Outperforming peers', color:T.gn, bg:alpha(T.gn, 0.08) },
  in_line: { label:'In line with peers',  color:T.yl, bg:alpha(T.am, 0.08) },
  below:   { label:'Below peer average',  color:T.rd, bg:alpha(T.rd, 0.08) },
  neutral: { label:'Reference only',      color:T.mt, bg:alpha(T.mt, 0.08) },
  no_data: { label:'No data',             color:T.mt, bg:alpha(T.mt, 0.05) },
}

const STATUS_LABEL = {
  above:'Outperforming', in_line:'In line', below:'Below average',
  neutral:'Reference', no_data:'No data',
}
const STATUS_COLOR = {
  above:'#10B981', in_line:'#F59E0B', below:'#EF4444',
  neutral:'#64748B', no_data:'#94A3B8',
}

function fmtVal(v, unit) {
  if (v === null || v === undefined) return '—'
  if (unit === 'days') return `${Math.round(v)} days`
  if (unit === '%')    return `${v.toFixed(1)}%`
  return `${v.toFixed(2)}x`
}

export default function BenchmarkReport({ client, ratios, industry, yearEnd, periodStart }) {
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')
  const [report,   setReport]   = useState(null)

  const handleGenerate = async () => {
    setLoading(true); setError('')
    try {
      const { data } = await generateBenchmark({
        company:  client?.name || '',
        industry: industry || client?.industry || 'Other',
        ratios:   ratios || {},
        year_end: yearEnd || '',
        period_start: periodStart || undefined,  // Phase 1 (Jul 2026)
      })
      setReport(data)
    } catch {
      setError('Could not generate the benchmark report. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadReport = () => {
    if (!report) return

    const ratioRows = report.comparison
      .filter(c => c.client_value !== null)
      .map(c => `
        <tr>
          <td style="padding:10px 14px;border-bottom:1px solid #F1F5F9;">${c.label}</td>
          <td style="padding:10px 14px;border-bottom:1px solid #F1F5F9;font-weight:700;">${fmtVal(c.client_value, c.unit)}</td>
          <td style="padding:10px 14px;border-bottom:1px solid #F1F5F9;color:#64748B;">${fmtVal(c.benchmark_value, c.unit)}</td>
          <td style="padding:10px 14px;border-bottom:1px solid #F1F5F9;font-weight:700;color:${STATUS_COLOR[c.status] || '#64748B'};">${STATUS_LABEL[c.status] || c.status}</td>
        </tr>`).join('')

    const narrativeHtml = (report.narrative || '')
      .split('\n').filter(Boolean)
      .map(p => `<p style="margin:0 0 14px;line-height:1.8;">${p}</p>`).join('')

    const today = new Date().toLocaleDateString('en-ZA', {
      day:'numeric', month:'long', year:'numeric',
    })

    // Phase 1 (Jul 2026): don't confidently call a stub/non-standard period
    // a "Year" in the downloaded report header.
    let periodWord = 'Year'
    if (periodStart && yearEnd) {
      const start = new Date(periodStart), end = new Date(yearEnd)
      if (end > start) {
        const months = Math.round((end - start) / (1000 * 60 * 60 * 24 * 30.4375))
        if (months < 11 || months > 13) periodWord = 'Period'
      }
    }

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>Business Intelligence Report — ${client?.name || 'Entity'}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Georgia,'Times New Roman',serif;color:#1a1a2e;background:#fff;padding:48px;max-width:860px;margin:0 auto}
    h1{font-size:20px;font-weight:700;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px}
    .meta{font-size:12px;color:#64748B;margin-bottom:8px}
    .divider{border:none;border-top:2px solid #0F172A;margin:20px 0 28px}
    .section-label{font-size:9px;font-weight:700;letter-spacing:2px;color:#94A3B8;text-transform:uppercase;margin-bottom:14px;padding-bottom:6px;border-bottom:1px solid #E2E8F0}
    table{width:100%;border-collapse:collapse;margin-bottom:36px;font-family:Arial,sans-serif;font-size:13px}
    thead tr{background:#F8FAFC;border-bottom:2px solid #E2E8F0}
    th{padding:10px 14px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:#64748B}
    .commentary{font-size:14px;color:#1a1a2e;margin-bottom:36px}
    .disclaimer{font-size:11px;color:#94A3B8;font-style:italic;padding-top:16px;border-top:1px solid #E2E8F0;line-height:1.6;margin-bottom:20px}
    .footer{font-size:10px;color:#CBD5E1;text-align:right}
    @media print{body{padding:24px}@page{margin:20mm}}
  </style>
</head>
<body>
  <h1>${(client?.name || 'Entity Name').toUpperCase()}</h1>
  <div class="meta">Business Intelligence Report &bull; ${periodWord} ended ${yearEnd || ''}</div>
  <div class="meta">Benchmarked against South African ${report.industry || 'Industry'} peers</div>
  <hr class="divider">

  <div class="section-label">Financial Ratio Comparison</div>
  <table>
    <thead>
      <tr>
        <th>Metric</th>
        <th>This Entity</th>
        <th>SA Peer Average</th>
        <th>Assessment</th>
      </tr>
    </thead>
    <tbody>${ratioRows}</tbody>
  </table>

  <div class="section-label">Commentary</div>
  <div class="commentary">${narrativeHtml}</div>

  <div class="disclaimer">
    Benchmarks are indicative South African industry averages derived from SARB, Stats SA, and SARS-published
    statistics. They should be used as a general guide alongside professional judgement and are not a substitute
    for a formal business valuation or strategic analysis.
  </div>
  <div class="footer">Generated by KaiLux &bull; ${today}</div>
</body>
</html>`

    const blob = new Blob([html], { type: 'text/html' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `${(client?.name || 'Entity').replace(/\s+/g, '_')}_BI_Report_${(yearEnd || '').replace(/\s+/g,'_')}.html`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{ marginTop:'32px', padding:'20px 24px',
      background:'#F8FAFC', border:'1px solid #E2E8F0',
      borderRadius:'12px', textAlign:'left' }}>

      <div style={{ display:'flex', justifyContent:'space-between',
        alignItems:'flex-start', gap:'12px', marginBottom: report ? '16px' : '4px',
        flexWrap:'wrap' }}>
        <div>
          <div style={{ fontSize:'13px', fontWeight:'700', color:'#0F172A', marginBottom:'4px' }}>
            <Icon name="chart" /> Business Intelligence Report
          </div>
          <div style={{ fontSize:'12px', color:T.mt, lineHeight:'1.6', maxWidth:'460px' }}>
            See how {client?.name || 'this client'} compares to South African
            {' '}{industry || client?.industry || 'industry'} peer benchmarks —
            a feature no other SA AFS tool offers.
          </div>
        </div>

        <div style={{ display:'flex', gap:'8px', flexShrink:0, flexWrap:'wrap' }}>
          {report && (
            <button onClick={handleDownloadReport}
              style={{ padding:'9px 16px', borderRadius:'8px',
                border:`1px solid ${T.bd}`, background:'white',
                color:'#374151', fontFamily:"'Outfit',sans-serif", fontSize:'12px',
                fontWeight:'700', cursor:'pointer', whiteSpace:'nowrap',
                display:'flex', alignItems:'center', gap:'6px' }}>
              ↓ Download Report
            </button>
          )}
          {!report && (
            <button onClick={handleGenerate} disabled={loading}
              style={{ padding:'9px 18px', borderRadius:'8px', border:'none',
                background: loading ? T.mt : T.prg,
                color:T.onpri, fontFamily:"'Outfit',sans-serif", fontSize:'12px',
                fontWeight:'700', cursor: loading ? 'not-allowed' : 'pointer',
                whiteSpace:'nowrap' }}>
              {loading ? 'Analysing...' : 'Generate BI Report →'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{ padding:'10px 14px', background:alpha(T.rd, 0.08),
          border:`1px solid ${alpha(T.rd, 0.2)}`, borderRadius:'8px',
          fontSize:'12px', color:T.rd, marginBottom:'10px' }}>
          {error}
        </div>
      )}

      {report && (
        <div>
          <div style={{ fontSize:'10px', color:T.mt, fontWeight:'700',
            letterSpacing:'1px', marginBottom:'10px' }}>
            COMPARED TO SA {report.industry?.toUpperCase()} BENCHMARKS
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)',
            gap:'8px', marginBottom:'18px' }}>
            {report.comparison.filter(c => c.client_value !== null).map(c => {
              const meta = STATUS_META[c.status] || STATUS_META.no_data
              return (
                <div key={c.key} style={{ background:meta.bg,
                  border:`1px solid ${alpha(meta.color, 0.19)}`, borderRadius:'8px',
                  padding:'10px 12px' }}>
                  <div style={{ fontSize:'9px', color:T.mt, fontWeight:'600',
                    letterSpacing:'0.5px', marginBottom:'4px' }}>
                    {c.label.toUpperCase()}
                  </div>
                  <div style={{ fontSize:'15px', fontWeight:'800', color:T.tx }}>
                    {fmtVal(c.client_value, c.unit)}
                  </div>
                  <div style={{ fontSize:'10px', color:T.mt, marginTop:'2px' }}>
                    Peer avg: {fmtVal(c.benchmark_value, c.unit)}
                  </div>
                  <div style={{ fontSize:'10px', fontWeight:'700', color:meta.color, marginTop:'4px' }}>
                    {meta.label}
                  </div>
                </div>
              )
            })}
          </div>

          <div style={{ fontSize:'10px', color:T.mt, fontWeight:'700',
            letterSpacing:'1px', marginBottom:'8px' }}>
            COMMENTARY
          </div>
          <div style={{ fontSize:'13px', color:'#374151', lineHeight:'1.8' }}>
            {report.narrative.split('\n').filter(Boolean).map((p, i) => (
              <p key={i} style={{ margin: i === 0 ? '0 0 10px' : '0 0 10px' }}>{p}</p>
            ))}
          </div>

          <div style={{ marginTop:'14px', display:'flex', justifyContent:'space-between',
            alignItems:'flex-end', flexWrap:'wrap', gap:'10px' }}>
            <div style={{ fontSize:'10px', color:'#94A3B8',
              fontStyle:'italic', lineHeight:'1.6', maxWidth:'480px' }}>
              Benchmarks are indicative South African industry averages and should be
              used as a general guide alongside professional judgement.
            </div>
            <button onClick={handleDownloadReport}
              style={{ padding:'8px 16px', borderRadius:'8px',
                border:`1px solid ${T.bd}`, background:'white',
                color:'#374151', fontFamily:"'Outfit',sans-serif", fontSize:'11px',
                fontWeight:'700', cursor:'pointer', whiteSpace:'nowrap',
                display:'flex', alignItems:'center', gap:'6px', flexShrink:0 }}>
              ↓ Download BI Report
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
